/**
 * Chat state store for managing conversations and messages
 */
import { create } from 'zustand';
import { Conversation, Message, conversationsApi } from '../api/conversations';
import { toast } from 'react-hot-toast';

interface ChatState {
  // Current conversation
  currentConversation: Conversation | null;
  conversations: Conversation[];
  isLoading: boolean;

  // Actions
  createConversation: (title?: string) => Promise<Conversation>;
  loadConversation: (conversationId: string) => Promise<void>;
  loadConversations: () => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  updateVerbosity: (conversationId: string, verbosity: 'concise' | 'detailed') => Promise<void>;
  clearCurrentConversation: () => void;
  addMessageToStore: (message: Message) => void;
  syncToClipboard: (conversationId: string) => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  currentConversation: null,
  conversations: [],
  isLoading: false,

  /**
   * Create a new conversation
   */
  createConversation: async (title?: string) => {
    try {
      set({ isLoading: true });
      const conversation = await conversationsApi.createConversation({ title });

      // Add to conversations list, avoiding duplicates
      set((state) => {
        const exists = state.conversations.some(c => c.id === conversation.id);
        if (exists) {
          return {
            currentConversation: conversation,
            isLoading: false
          };
        }
        return {
          conversations: [conversation, ...state.conversations],
          currentConversation: conversation,
          isLoading: false
        };
      });

      return conversation;
    } catch (error: any) {
      console.error('Failed to create conversation:', error);
      toast.error('Failed to create conversation');
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Load a conversation with its messages
   */
  loadConversation: async (conversationId: string) => {
    try {
      set({ isLoading: true });
      const conversation = await conversationsApi.getConversation(conversationId);

      set({
        currentConversation: conversation,
        isLoading: false
      });
    } catch (error: any) {
      console.error('Failed to load conversation:', error);
      toast.error('Failed to load conversation');
      set({ isLoading: false });
      throw error;
    }
  },

  /**
   * Load list of user's conversations
   */
  loadConversations: async () => {
    try {
      const conversations = await conversationsApi.listConversations(50, 0);
      set({ conversations });
    } catch (error: any) {
      console.error('Failed to load conversations:', error);
      toast.error('Failed to load conversations');
    }
  },

  /**
   * Delete a conversation
   */
  deleteConversation: async (conversationId: string) => {
    try {
      await conversationsApi.deleteConversation(conversationId);

      // Remove from store
      set((state) => ({
        conversations: state.conversations.filter(c => c.id !== conversationId),
        currentConversation: state.currentConversation?.id === conversationId
          ? null
          : state.currentConversation
      }));

      toast.success('Conversation deleted');
    } catch (error: any) {
      console.error('Failed to delete conversation:', error);
      toast.error('Failed to delete conversation');
      throw error;
    }
  },

  /**
   * Update conversation verbosity
   */
  updateVerbosity: async (conversationId: string, verbosity: 'concise' | 'detailed') => {
    try {
      const updatedConversation = await conversationsApi.updateVerbosity(conversationId, { verbosity });

      // Update in store - preserve existing messages
      set((state) => {
        const newState = {
          currentConversation: state.currentConversation?.id === conversationId
            ? {
                ...state.currentConversation,
                response_verbosity: verbosity,
                updated_at: updatedConversation.updated_at
              }
            : state.currentConversation,
          conversations: state.conversations.map(c =>
            c.id === conversationId ? { ...c, response_verbosity: verbosity } : c
          )
        };
        return newState;
      });
    } catch (error: any) {
      console.error('Failed to update verbosity:', error);
      toast.error('Failed to update verbosity setting');
      throw error;
    }
  },

  /**
   * Clear current conversation (start fresh)
   */
  clearCurrentConversation: () => {
    set({ currentConversation: null });
  },

  /**
   * Add or update a message in the current conversation in the store (optimistic update)
   * If a message with the same ID exists, it will be updated instead of appended
   */
  addMessageToStore: (message: Message) => {
    set((state) => {
      if (!state.currentConversation) return state;

      const messages = state.currentConversation.messages || [];
      const existingIndex = messages.findIndex(m => m.id === message.id);

      let updatedMessages: Message[];
      if (existingIndex >= 0) {
        // Update existing message (for streaming)
        updatedMessages = [...messages];
        updatedMessages[existingIndex] = message;
      } else {
        // Append new message
        updatedMessages = [...messages, message];
      }

      return {
        currentConversation: {
          ...state.currentConversation,
          messages: updatedMessages
        }
      };
    });
  },

  /**
   * Sync conversation to clipboard
   */
  syncToClipboard: async (conversationId: string) => {
    try {
      await conversationsApi.syncToClipboard(conversationId);
      toast.success('Conversation saved to clipboard');
    } catch (error: any) {
      console.error('Failed to sync to clipboard:', error);
      toast.error('Failed to save to clipboard');
      throw error;
    }
  }
}));
